import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import { buildSessionHeaders, getClientAccessToken } from '../utils/sessionAuth';
import {
  getAllowedBillUploadFormats,
  type BillUploadFormat,
  type BillUploadSettings,
} from '../constants/uiVisibility';
import {
  API_CACHE_TTL,
  buildApiCacheKey,
  cachedApiGet,
  clearApiRequestCache,
} from './apiRequestCache';
import { invalidateAllWorkspaceReadCaches } from './workspaceCache';
import {
  BILL_UPLOAD_DISABLED_MESSAGE,
  isBillOcrDocumentType,
  isBillUploadEnabled,
} from '../constants/uiVisibility';

const assertBillUploadAllowed = (documentType?: string | null) => {
  if (!isBillUploadEnabled() && isBillOcrDocumentType(documentType || 'bill')) {
    throw new Error(BILL_UPLOAD_DISABLED_MESSAGE);
  }
};

const buildDocumentQueryKey = (params: Record<string, unknown> = {}): string => {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${String(value)}`);
  return entries.join('&');
};

const getAxiosResponse = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  return (error as { response?: { status?: number; data?: unknown; headers?: Record<string, string> } })
    .response;
};

const isAxiosRateLimited = (error: unknown): boolean =>
  getAxiosResponse(error)?.status === 429;

const isGatewayTimeoutStatus = (status?: number) =>
  status === 502 || status === 503 || status === 504;

const looksLikeHtmlErrorPage = (value: string) =>
  /<\s*html[\s>]|504\s+Gateway\s+Time-?out|502\s+Bad\s+Gateway/i.test(value);

const GATEWAY_TIMEOUT_MESSAGE =
  'The upload timed out before the server finished accepting the file. Bills already accepted continue processing in the background — retry only the ones that failed.';

const CLIENT_TIMEOUT_MESSAGE =
  'The upload took too long and was stopped. Already accepted bills keep processing in the background — retry failed files.';

const formatAxiosError = (error: unknown): Error => {
  if (
    error &&
    typeof error === 'object' &&
    'code' in error &&
    (error as { code?: string }).code === 'ECONNABORTED'
  ) {
    return new Error(CLIENT_TIMEOUT_MESSAGE);
  }

  const response = getAxiosResponse(error);
  if (response) {
    const status = response.status;
    const data = response.data;

    if (isGatewayTimeoutStatus(status)) {
      return new Error(GATEWAY_TIMEOUT_MESSAGE);
    }

    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
    ) {
      const message = (data as { message: string }).message.trim();
      if (looksLikeHtmlErrorPage(message)) {
        return new Error(GATEWAY_TIMEOUT_MESSAGE);
      }
      return new Error(message || `Request failed (${status})`);
    }

    if (typeof data === 'string' && data.trim().length > 0) {
      if (looksLikeHtmlErrorPage(data)) {
        return new Error(GATEWAY_TIMEOUT_MESSAGE);
      }
      return new Error(data);
    }

    if (status === 429) {
      return new Error('Too many requests. Please wait a moment and try again.');
    }
    if (status) {
      return new Error(`Request failed (${status})`);
    }
  }

  if (error instanceof Error && looksLikeHtmlErrorPage(error.message)) {
    return new Error(GATEWAY_TIMEOUT_MESSAGE);
  }

  return error instanceof Error ? error : new Error('Request failed');
};

/** Upload ACK should finish quickly; OCR continues in the background after 201. */
const DOCUMENT_UPLOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_DOCUMENT_UPLOAD_TIMEOUT_MS || 90000
);

const withRateLimitRetry = async <T>(fn: () => Promise<T>): Promise<T> => {
  const maxAttempts = 3;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      if (!isAxiosRateLimited(error) || attempt === maxAttempts - 1) {
        throw formatAxiosError(error);
      }
      const retryAfterHeader = getAxiosResponse(error)?.headers?.['retry-after'];
      const retryAfterSeconds = Number(retryAfterHeader);
      const delayMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? retryAfterSeconds * 1000
        : Math.min(1500 * 2 ** attempt, 8000);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  throw new Error('Request failed');
};

export const SUPPORTED_DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/tiff',
  'image/bmp',
]);

// Same limits as the API (backend UPLOAD_MAX_FILE_MB / UPLOAD_BULK_MAX_FILES).
const parsePositiveInt = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
export const UPLOAD_MAX_FILE_MB = parsePositiveInt(process.env.REACT_APP_UPLOAD_MAX_FILE_MB, 10);
export const UPLOAD_BULK_MAX_FILES = parsePositiveInt(process.env.REACT_APP_UPLOAD_BULK_MAX_FILES, 20);

export const SUPPORTED_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.tif',
  '.tiff',
  '.bmp',
];

export const SUPPORTED_DOCUMENT_ACCEPT_MAP: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/tiff': ['.tif', '.tiff'],
  'image/bmp': ['.bmp'],
};

// ZIP is not accepted by the API, and one rejected file used to fail the whole batch.
export const SUPPORTED_DOCUMENT_TYPE_LABEL = 'PDF, JPG, JPEG, PNG, WEBP, TIFF, BMP';

// Bill uploads: formats an admin can allow or block (Admin dashboard > Platform Features).
const BILL_FORMAT_ACCEPT: Record<BillUploadFormat, Record<string, string[]>> = {
  spreadsheet: {
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/csv': ['.csv'],
  },
  pdf: { 'application/pdf': ['.pdf'] },
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/tiff': ['.tif', '.tiff'],
    'image/bmp': ['.bmp'],
  },
};

const BILL_FORMAT_LABELS: Record<BillUploadFormat, string> = {
  spreadsheet: 'XLS, XLSX, CSV',
  pdf: 'PDF',
  image: 'JPG, JPEG, PNG, WEBP, TIFF, BMP',
};

const BILL_FORMAT_ORDER: BillUploadFormat[] = ['spreadsheet', 'pdf', 'image'];

export const resolveBillUploadFormat = (file: { name?: string; type?: string }): BillUploadFormat | null => {
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  for (const format of BILL_FORMAT_ORDER) {
    const accept = BILL_FORMAT_ACCEPT[format];
    if (accept[type] || Object.values(accept).some((exts) => exts.some((ext) => name.endsWith(ext)))) {
      return format;
    }
  }
  return null;
};

/** Dropzone accept map for the bill formats currently allowed. */
export const getBillUploadAccept = (
  formats: Record<BillUploadFormat, boolean> = getAllowedBillUploadFormats()
): Record<string, string[]> => Object.assign(
  {},
  ...BILL_FORMAT_ORDER.filter((format) => formats[format]).map((format) => BILL_FORMAT_ACCEPT[format])
);

/** "XLS, XLSX, CSV, PDF" style label for the bill formats currently allowed. */
export const getBillUploadTypeLabel = (
  formats: Record<BillUploadFormat, boolean> = getAllowedBillUploadFormats()
): string => BILL_FORMAT_ORDER.filter((format) => formats[format]).map((format) => BILL_FORMAT_LABELS[format]).join(', ')
  || 'none';

export interface Document {
  id: string;
  fileName: string;
  originalName: string;
  documentType: string;
  status: 'uploaded' | 'queued' | 'processing' | 'processed' | 'failed' | 'duplicate';
  createdAt: string;
  extractedData?: {
    amount: number;
    vendor: {
      name: string;
      address?: string;
      phone?: string;
      email?: string;
    };
    date: string;
    description: string;
    category: string;
    subcategory?: string;
    items?: Array<{
      name: string;
      quantity: number;
      unit: string;
      price: number;
      total: number;
    }>;
    tax?: {
      cgst: number;
      sgst: number;
      igst: number;
      total: number;
    };
    paymentMethod?: string;
    referenceNumber?: string;
    /** RC-4 compatibility alias — mirrors referenceNumber on new saves */
    invoice_number?: string;
    gstin?: string;
    /** RC-4 compatibility alias — mirrors gstin on new saves */
    seller_gstin?: string;
    fieldContract?: {
      version?: string;
      appliedAt?: string;
    };
    fieldProvenance?: {
      version?: string;
      recordedAt?: string;
      fields?: Record<string, {
        value?: string | number | null;
        winner?: { source?: string; stage?: string };
        modifications?: Array<{
          stage: string;
          source: string;
          from?: unknown;
          to?: unknown;
          at: string;
        }>;
      }>;
    };
    ocr_validation?: OcrValidationPayload;
    ocr_metadata?: Record<string, unknown>;
    /** Utility-bill activity fields (Datalab / NIM / ai-model extraction) */
    consumption_quantity?: number | null;
    consumption_unit?: string | null;
    consumption?: {
      electricity_kwh?: number | null;
      water_kl?: number | null;
      lpg_kg?: number | null;
      fuel_litres?: number | null;
      distance_km?: number | null;
    };
    consumer_number?: string | null;
    meter_number?: string | null;
    billing_period_start?: string | null;
    billing_period_end?: string | null;
    due_date?: string | null;
    grand_total?: number | null;
    current_charges?: number | null;
    energy_charges?: number | null;
    fixed_charges?: number | null;
    electricity_duty?: number | null;
    meter?: {
      current_reading?: number | null;
      previous_reading?: number | null;
      multiplying_factor?: number | null;
    };
  };
  duplicateDetection?: {
    isDuplicate: boolean;
    duplicateType: string | null;
    decisionTier?: string | null;
    matchedDocumentId: string;
    duplicateReasons: string[];
  };
  processingResults?: {
    confidence: number;
    processingTime: number;
    errors: string[];
    warnings: string[];
    accuracyReport?: { overall?: number } | null;
    ocrValidation?: OcrValidationPayload | null;
    billQualityRemediation?: BillQualityRemediationPayload | null;
  };
  carbonFootprint?: {
    co2Emissions: number;
    emissionFactor: number;
    calculationMethod: string;
    sustainabilityScore: number;
    /** Spend-based (₹ only) estimate, kept out of the inventory total until quantities are confirmed. */
    screeningCo2Emissions?: number;
    emissionBreakdown?: {
      scope1?: number;
      scope2?: number;
      scope3?: number;
    };
  };
  notes?: string;
  tags?: string[];
  metadata?: {
    transactionMapping?: 'company' | 'product';
    ocr?: {
      strategy?: string;
      engines?: string[];
      thermalDetection?: { is_thermal?: boolean; receipt_type?: string };
      blurDetected?: boolean;
      processingTimeMs?: number;
      pageCount?: number;
      gateRejectionReason?: string;
      rejectionReason?: string;
    };
    [key: string]: unknown;
  };
  ocrValidation?: OcrValidationPayload | null;
  extractedDataVerification?: {
    status?: 'pending' | 'verified';
    verifiedAt?: string;
    acceptedForEmissions?: boolean;
    correctionCount?: number;
  };
}

export interface OcrUserAction {
  action: string;
  message: string;
  missing_fields?: string[];
  problematic_sections?: string[];
  tips?: string[];
}

export interface OcrValidationPayload {
  invoice_confidence?: number;
  ocr_quality_score?: number;
  low_confidence?: boolean;
  unreadable?: boolean;
  missing_fields?: string[];
  problematic_sections?: string[];
  blur_detected?: boolean;
  thermal_detection?: { is_thermal?: boolean; receipt_type?: string };
  gate_rejection_reason?: string;
  user_action?: OcrUserAction | null;
}

export interface BillQualityRemediationPayload {
  band: 'review_required';
  qualityScore: number;
  qualityPct: number;
  minProcessingThreshold: number;
  carbonAnalysisThreshold: number;
  activityUpliftThreshold: number;
  summary: string;
  suggestions: Array<{
    id: string;
    severity: 'important' | 'recommended' | 'info';
    message: string;
    action: 'verify' | 'reupload' | 'metered_upload';
  }>;
  capabilities: {
    processed: boolean;
    spendCarbon: boolean;
    activityUplift: boolean;
    requiresVerification: boolean;
  };
}

export interface OcrBenchmarkReport {
  id: string;
  createdAt?: string;
  casesRun?: number;
  fieldAccuracy?: Record<string, number>;
  meanInvoiceConfidence?: number;
  failedCount?: number;
}

export interface OcrBenchmarkSummary {
  cases_run?: number;
  cases_succeeded?: number;
  cases_passed?: number;
  cases_failed?: number;
  pass_rate?: number;
  field_accuracy?: Record<string, number | null>;
  mean_invoice_confidence?: number;
  mean_processing_time_ms?: number;
  ocr_confidence_summary?: { min?: number; max?: number; mean?: number };
  processing_time_summary?: { min_ms?: number; max_ms?: number; mean_ms?: number; total_ms?: number };
  failed_extractions?: unknown[];
  regression?: OcrRegressionReport;
}

export interface OcrAutomationReport {
  id: string;
  createdAt?: string;
  runType?: string;
  casesRun?: number;
  casesPassed?: number;
  passRate?: number;
  meanInvoiceConfidence?: number;
  failedCount?: number;
  regressionDegraded?: boolean;
  fieldAccuracy?: Record<string, number>;
}

export interface OcrRegressionReport {
  has_baseline?: boolean;
  degraded?: boolean;
  confidence_delta?: number;
  confidence_regression?: boolean;
  field_regressions?: Array<{ field: string; baseline: number; current: number; delta: number }>;
  field_improvements?: Array<{ field: string; baseline: number; current: number; delta: number }>;
}

export interface OcrAutomationMetrics {
  casesRun?: number;
  casesPassed?: number;
  casesFailed?: number;
  passRate?: number;
  fieldAccuracy?: Record<string, number | null>;
  meanInvoiceConfidence?: number;
  ocrConfidenceSummary?: { min?: number; max?: number; mean?: number };
  processingTimeSummary?: { min_ms?: number; max_ms?: number; mean_ms?: number; total_ms?: number };
  meanProcessingTimeMs?: number;
}

export interface OcrFailedInvoice {
  id?: string;
  file?: string;
  error?: string;
  failed_fields?: string[];
  failure_reasons?: string[];
  invoice_confidence?: number;
}

export interface DocumentStatistics {
  totalDocuments: number;
  documentsByType: Record<string, number>;
  documentsByStatus: Record<string, number>;
  totalAmount: number;
  totalCarbonFootprint: number;
  duplicateCount: number;
  averageProcessingTime: number;
  documentsByMonth: Record<string, number>;
}

export interface UploadResponse {
  success: boolean;
  message: string;
  document: Document;
}

export interface BulkUploadResponse {
  success: boolean;
  message: string;
  data: {
    documents: Document[];
    processing: {
      mode: string;
      status?: string;
      jobs: Array<{
        documentId: string;
        jobId?: string | null;
        mode: string;
        deferred?: boolean;
      }>;
    };
  };
}

export interface DocumentsResponse {
  success: boolean;
  documents: Document[];
  pagination: {
    current: number;
    pages: number;
    total: number;
  };
}

export interface StatisticsResponse {
  success: boolean;
  statistics: DocumentStatistics;
}

/** API may return Mongo `_id`; UI expects string `id`. */
const normalizeDocumentRecord = <T extends Record<string, unknown>>(raw: T): T & { id: string } => {
  const id =
    (typeof raw.id === 'string' && raw.id) ||
    (raw._id != null ? String(raw._id) : '');
  const { _id: _drop, ...rest } = raw as T & { _id?: unknown };
  return { ...rest, id } as T & { id: string };
};

const normalizeDocumentList = (documents: unknown[]): Document[] =>
  (documents || []).map((doc) =>
    normalizeDocumentRecord(doc as Record<string, unknown>),
  ) as unknown as Document[];

class DocumentService {
  private baseURL = API_BASE_URL;

  private getAuthHeaders(extra: Record<string, string> = {}) {
    const headers: Record<string, string> = { ...extra };
    const token = getClientAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return buildSessionHeaders(headers);
  }

  private uploadRequestConfig(timeout = DOCUMENT_UPLOAD_TIMEOUT_MS) {
    return {
      timeout,
      withCredentials: true as const,
      headers: this.getAuthHeaders(),
    };
  }

  private invalidateDocumentsCache(): void {
    invalidateAllWorkspaceReadCaches();
  }

  private documentsListCacheKey(
    path: string,
    params: Record<string, unknown> = {},
  ): string {
    return buildApiCacheKey('GET', path, buildDocumentQueryKey(params));
  }

  /**
   * Upload a document
   */
  async uploadDocument(
    file: File, 
    documentType: string = 'bill', 
    notes?: string,
    transactionMapping: 'company' | 'product' = 'company',
    selectedProducts: string[] = [],
    workflowMeta?: {
      sourceWorkflow?: string;
      linkedSourceId?: string;
      linkedTransactionId?: string;
      linkedMessageId?: string;
      gpsLatitude?: string | number;
      gpsLongitude?: string | number;
      gpsAccuracyMeters?: string | number;
      gpsCapturedAt?: string;
      gpsEmbeddedInImage?: boolean;
      /** True when capture proceeded without GPS (permission denied / unavailable). */
      gpsMissing?: boolean;
    }
  ): Promise<UploadResponse> {
    assertBillUploadAllowed(documentType);
    const formData = new FormData();
    
    formData.append('document', file);
    formData.append('documentType', documentType);
    formData.append('transactionMapping', transactionMapping);
    if (Array.isArray(selectedProducts) && selectedProducts.length > 0) {
      formData.append('selectedProducts', JSON.stringify(selectedProducts));
    }
    if (workflowMeta?.sourceWorkflow) {
      formData.append('sourceWorkflow', workflowMeta.sourceWorkflow);
    }
    if (workflowMeta?.linkedSourceId) {
      formData.append('linkedSourceId', workflowMeta.linkedSourceId);
    }
    if (workflowMeta?.linkedTransactionId) {
      formData.append('linkedTransactionId', workflowMeta.linkedTransactionId);
    }
    if (workflowMeta?.linkedMessageId) {
      formData.append('linkedMessageId', workflowMeta.linkedMessageId);
    }
    if (workflowMeta?.gpsLatitude != null && workflowMeta?.gpsLongitude != null) {
      formData.append('gpsLatitude', String(workflowMeta.gpsLatitude));
      formData.append('gpsLongitude', String(workflowMeta.gpsLongitude));
    }
    if (workflowMeta?.gpsAccuracyMeters != null) {
      formData.append('gpsAccuracyMeters', String(workflowMeta.gpsAccuracyMeters));
    }
    if (workflowMeta?.gpsCapturedAt) {
      formData.append('gpsCapturedAt', workflowMeta.gpsCapturedAt);
    }
    if (workflowMeta?.gpsEmbeddedInImage) {
      formData.append('gpsEmbeddedInImage', 'true');
    }
    if (workflowMeta?.gpsMissing) {
      formData.append('gpsMissing', 'true');
    }
    if (notes) {
      formData.append('notes', notes);
    }
    const response = await withRateLimitRetry(() =>
      axios.post(`${this.baseURL}/documents/upload`, formData, this.uploadRequestConfig()),
    );

    this.invalidateDocumentsCache();

    const data = response.data as UploadResponse & { document?: Record<string, unknown> };
    if (data?.success && data.document) {
      return {
        ...data,
        document: normalizeDocumentRecord(data.document) as UploadResponse['document'],
      };
    }
    return data;
  }

  /**
   * Upload multiple documents in one request for parallel background processing.
   */
  async uploadDocumentsBulk(
    files: File[],
    documentType: string = 'bill',
    notes?: string,
    transactionMapping: 'company' | 'product' = 'company',
    selectedProducts: string[] = [],
    workflowMeta?: {
      sourceWorkflow?: string;
      linkedSourceId?: string;
      linkedTransactionId?: string;
      linkedMessageId?: string;
      gpsLatitude?: string | number;
      gpsLongitude?: string | number;
      gpsAccuracyMeters?: string | number;
      gpsCapturedAt?: string;
      gpsEmbeddedInImage?: boolean;
      gpsMissing?: boolean;
    }
  ): Promise<BulkUploadResponse> {
    assertBillUploadAllowed(documentType);
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('documents', file);
    });
    formData.append('documentType', documentType);
    formData.append('transactionMapping', transactionMapping);
    if (Array.isArray(selectedProducts) && selectedProducts.length > 0) {
      formData.append('selectedProducts', JSON.stringify(selectedProducts));
    }
    if (workflowMeta?.sourceWorkflow) {
      formData.append('sourceWorkflow', workflowMeta.sourceWorkflow);
    }
    if (workflowMeta?.linkedSourceId) {
      formData.append('linkedSourceId', workflowMeta.linkedSourceId);
    }
    if (workflowMeta?.linkedTransactionId) {
      formData.append('linkedTransactionId', workflowMeta.linkedTransactionId);
    }
    if (workflowMeta?.linkedMessageId) {
      formData.append('linkedMessageId', workflowMeta.linkedMessageId);
    }
    if (workflowMeta?.gpsLatitude != null && workflowMeta?.gpsLongitude != null) {
      formData.append('gpsLatitude', String(workflowMeta.gpsLatitude));
      formData.append('gpsLongitude', String(workflowMeta.gpsLongitude));
    }
    if (workflowMeta?.gpsAccuracyMeters != null) {
      formData.append('gpsAccuracyMeters', String(workflowMeta.gpsAccuracyMeters));
    }
    if (workflowMeta?.gpsCapturedAt) {
      formData.append('gpsCapturedAt', workflowMeta.gpsCapturedAt);
    }
    if (workflowMeta?.gpsEmbeddedInImage) {
      formData.append('gpsEmbeddedInImage', 'true');
    }
    if (workflowMeta?.gpsMissing) {
      formData.append('gpsMissing', 'true');
    }
    if (notes) {
      formData.append('notes', notes);
    }

    const response = await withRateLimitRetry(() =>
      axios.post(`${this.baseURL}/documents/upload/bulk`, formData, this.uploadRequestConfig()),
    );

    this.invalidateDocumentsCache();

    const data = response.data as BulkUploadResponse & {
      data?: { documents?: Array<Record<string, unknown>> };
    };
    if (data?.success && Array.isArray(data.data?.documents)) {
      return {
        ...data,
        data: {
          ...data.data,
          documents: normalizeDocumentList(data.data.documents) as Document[],
        },
      };
    }
    return data;
  }

  /**
   * Bill upload settings chosen by an admin: bill reading on/off and the accepted bill formats.
   */
  async getUploadSettings(): Promise<BillUploadSettings | null> {
    const response = await axios.get(`${this.baseURL}/documents/upload-settings`, {
      headers: this.getAuthHeaders(),
    });
    return response.data?.success ? (response.data.data as BillUploadSettings) : null;
  }

  /**
   * Get all documents with pagination and filters
   */
  async getDocuments(params: {
    page?: number;
    limit?: number;
    status?: string;
    documentType?: string;
    startDate?: string;
    endDate?: string;
  } = {}): Promise<DocumentsResponse> {
    const cacheKey = this.documentsListCacheKey('/documents', params);
    return cachedApiGet(cacheKey, API_CACHE_TTL.documentsListMs, async () => {
      const response = await withRateLimitRetry(() =>
        axios.get(`${this.baseURL}/documents`, {
          params,
          headers: this.getAuthHeaders(),
        }),
      );

      const data = response.data as DocumentsResponse;
      if (data?.success && Array.isArray(data.documents)) {
        return { ...data, documents: normalizeDocumentList(data.documents) };
      }
      return data;
    });
  }

  /**
   * Get a specific document by ID
   */
  async getDocument(id: string, options?: { skipCache?: boolean }): Promise<{ success: boolean; document: Document }> {
    const cacheKey = buildApiCacheKey('GET', `/documents/${id}`);
    if (options?.skipCache) {
      clearApiRequestCache(cacheKey);
    }
    return cachedApiGet(cacheKey, API_CACHE_TTL.documentsDetailMs, async () => {
      const response = await withRateLimitRetry(() =>
        axios.get(`${this.baseURL}/documents/${id}`, {
          headers: this.getAuthHeaders(),
        }),
      );
      const data = response.data as { success: boolean; document: Document & { _id?: string } };
      if (data?.success && data.document) {
        return {
          ...data,
          document: normalizeDocumentRecord(data.document as unknown as Record<string, unknown>) as unknown as Document,
        };
      }
      return data;
    });
  }

  /**
   * Download a document
   */
  async downloadDocument(id: string): Promise<Blob> {
    const response = await axios.get(`${this.baseURL}/documents/${id}/download`, {
      responseType: 'blob',
      headers: this.getAuthHeaders(),
    });

    return response.data;
  }

  /**
   * Verify extracted bill fields and accept for carbon calculations
   */
  async verifyExtractedData(
    id: string,
    payload: {
      vendorName: string;
      date: string;
      gstin?: string;
      invoiceNumber: string;
      category?: string;
      fuelType?: string;
      electricityUnitPriceInr?: number;
      waterUnitPriceInr?: number;
      wasteUnitCostInr?: number;
      fuelUnitPriceInr?: number;
      activityQuantity?: number;
      activityUnit?: string;
      reviewConfirmed?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    document: Document;
    verification?: Document['extractedDataVerification'];
    transactionCreation?: {
      created: number;
      skippedDuplicates: number;
    };
    warnings?: string[];
  }> {
    const response = await withRateLimitRetry(() =>
      axios.post(`${this.baseURL}/documents/${id}/verify-extracted-data`, payload, {
        headers: this.getAuthHeaders(),
      }),
    );
    this.invalidateDocumentsCache();
    clearApiRequestCache(buildApiCacheKey('GET', `/documents/${id}`));

    const data = response.data as {
      success: boolean;
      message: string;
      document: Document & { _id?: string };
      verification?: Document['extractedDataVerification'];
      transactionCreation?: { created: number; skippedDuplicates: number };
      warnings?: string[];
    };

    if (data?.success && data.document) {
      return {
        ...data,
        document: normalizeDocumentRecord(data.document as unknown as Record<string, unknown>) as unknown as Document,
      };
    }
    return data;
  }

  /**
   * Update document metadata
   */
  async updateDocument(
    id: string, 
    updates: {
      notes?: string;
      tags?: string[];
      documentType?: string;
    }
  ): Promise<{ success: boolean; document: Document }> {
    const response = await withRateLimitRetry(() =>
      axios.put(`${this.baseURL}/documents/${id}`, updates, {
        headers: this.getAuthHeaders(),
      }),
    );
    this.invalidateDocumentsCache();
    clearApiRequestCache(buildApiCacheKey('GET', `/documents/${id}`));

    const data = response.data as { success: boolean; document: Document & { _id?: string } };
    if (data?.success && data.document) {
      return {
        ...data,
        document: normalizeDocumentRecord(data.document as unknown as Record<string, unknown>) as unknown as Document,
      };
    }
    return data;
  }

  /**
   * Delete a document
   */
  async deleteDocument(id: string): Promise<{ success: boolean; message: string }> {
    const response = await withRateLimitRetry(() =>
      axios.delete(`${this.baseURL}/documents/${id}`, {
        withCredentials: true,
        headers: this.getAuthHeaders(),
      }),
    );

    this.invalidateDocumentsCache();
    clearApiRequestCache(buildApiCacheKey('GET', `/documents/${id}`));

    return response.data;
  }

  /**
   * Reprocess a document
   */
  async reprocessDocument(id: string): Promise<{ success: boolean; message: string; result?: any }> {
    const response = await withRateLimitRetry(() =>
      axios.post(`${this.baseURL}/documents/${id}/reprocess`, null, {
        headers: this.getAuthHeaders(),
      }),
    );
    this.invalidateDocumentsCache();
    return response.data;
  }

  /**
   * Get document statistics
   */
  async getStatistics(params: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<StatisticsResponse> {
    const cacheKey = this.documentsListCacheKey('/documents/statistics/overview', params);
    return cachedApiGet(cacheKey, API_CACHE_TTL.documentsStatsMs, async () => {
      const response = await withRateLimitRetry(() =>
        axios.get(`${this.baseURL}/documents/statistics/overview`, {
          params,
          headers: this.getAuthHeaders(),
        }),
      );
      return response.data;
    });
  }

  /**
   * Get duplicate documents
   */
  async getDuplicates(params: {
    page?: number;
    limit?: number;
  } = {}): Promise<DocumentsResponse> {
    const cacheKey = this.documentsListCacheKey('/documents/duplicates', params);
    return cachedApiGet(cacheKey, API_CACHE_TTL.documentsListMs, async () => {
      const response = await withRateLimitRetry(() =>
        axios.get(`${this.baseURL}/documents/duplicates`, {
          params,
          headers: this.getAuthHeaders(),
        }),
      );

      const data = response.data as DocumentsResponse;
      if (data?.success && Array.isArray(data.documents)) {
        return { ...data, documents: normalizeDocumentList(data.documents) };
      }
      return data;
    });
  }

  /**
   * Create a download link for a document
   */
  createDownloadLink(blob: Blob, filename: string): string {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return url;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Human-readable document status for badges.
   */
  formatStatusLabel(status: string): string {
    if (!status) {
      return '';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  /**
   * Document status badge palette (solid chip fill + contrasting text/icon).
   */
  getStatusPalette(status: string): { background: string; text: string } {
    switch (status) {
      case 'queued':
        return { background: '#7C3AED', text: '#FFFFFF' };
      case 'processing':
        return { background: '#0288D1', text: '#FFFFFF' };
      case 'processed':
        return { background: '#16A34A', text: '#FFFFFF' };
      case 'duplicate':
        return { background: '#FFC107', text: '#111827' };
      case 'failed':
        return { background: '#DC2626', text: '#FFFFFF' };
      case 'uploaded':
      default:
        return { background: '#E5E7EB', text: '#374151' };
    }
  }

  /**
   * Chip styles for document status badges.
   */
  getStatusChipSx(status: string): {
    bgcolor: string;
    color: string;
    border: string;
    fontWeight: number;
    '& .MuiChip-label': { color: string };
  } {
    const palette = this.getStatusPalette(status);
    return {
      bgcolor: palette.background,
      color: palette.text,
      border: `1px solid ${palette.text}33`,
      fontWeight: 600,
      '& .MuiChip-label': { color: palette.text },
    };
  }

  /**
   * Icon color for document status. Uses the solid chip hue.
   * Uploaded uses dark grey so the icon stays readable on white.
   */
  getStatusIconSx(status: string): { color: string } {
    const palette = this.getStatusPalette(status);
    if (status === 'uploaded') {
      return { color: palette.text };
    }
    return { color: palette.background };
  }

  /**
   * @deprecated Use getStatusPalette / getStatusChipSx. Kept for callers that still expect a token.
   */
  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    switch (status) {
      case 'processed':
        return 'success';
      case 'failed':
        return 'error';
      case 'duplicate':
        return 'warning';
      case 'processing':
        return 'info';
      case 'queued':
        return 'secondary';
      default:
        return 'default';
    }
  }

  /**
   * Get document type icon
   */
  getDocumentTypeIcon(documentType: string): string {
    switch (documentType) {
      case 'bill':
        return '📄';
      case 'receipt':
        return '🧾';
      case 'invoice':
        return '📋';
      case 'statement':
        return '📊';
      default:
        return '📄';
    }
  }

  async getOcrBenchmarkReports(): Promise<{ success: boolean; reports: OcrBenchmarkReport[] }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/benchmark/reports`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getLatestOcrBenchmark(): Promise<{ success: boolean; report: OcrBenchmarkSummary; results?: unknown[] }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/benchmark/latest`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async runOcrBenchmark(options?: {
    writeBaseline?: boolean;
    workers?: number;
  }): Promise<{ success: boolean; report: OcrBenchmarkReport }> {
    const response = await axios.post(
      `${this.baseURL}/documents/ocr/benchmark/run`,
      { writeBaseline: options?.writeBaseline, workers: options?.workers },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getOcrAutomationReports(): Promise<{ success: boolean; reports: OcrAutomationReport[] }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/reports`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getLatestOcrAutomation(): Promise<{
    success: boolean;
    report: OcrBenchmarkSummary;
    results?: unknown[];
  }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/latest`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async runOcrAutomation(options?: {
    workers?: number;
    writeBaseline?: boolean;
    folderScan?: string;
    caseIds?: string[];
  }): Promise<{ success: boolean; report: OcrAutomationReport }> {
    const response = await axios.post(
      `${this.baseURL}/documents/ocr/automation/run`,
      options || {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async rerunFailedOcrTests(): Promise<{ success: boolean; report: OcrAutomationReport }> {
    const response = await axios.post(
      `${this.baseURL}/documents/ocr/automation/rerun-failed`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getOcrAutomationMetrics(reportId?: string): Promise<{ success: boolean; metrics: OcrAutomationMetrics }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/metrics`, {
      headers: this.getAuthHeaders(),
      params: reportId ? { reportId } : undefined,
    });
    return response.data;
  }

  async getOcrFailedInvoices(reportId?: string): Promise<{
    success: boolean;
    failedInvoices: OcrFailedInvoice[];
    fieldFailureCounts: Record<string, number>;
    lowConfidence: OcrFailedInvoice[];
  }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/failed`, {
      headers: this.getAuthHeaders(),
      params: reportId ? { reportId } : undefined,
    });
    return response.data;
  }

  async getOcrRegression(reportId?: string): Promise<{ success: boolean; regression: OcrRegressionReport }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/regression`, {
      headers: this.getAuthHeaders(),
      params: reportId ? { reportId } : undefined,
    });
    return response.data;
  }

  async getOcrFailureAnalysis(reportId?: string): Promise<{ success: boolean; analysis: Record<string, unknown> }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/failure-analysis`, {
      headers: this.getAuthHeaders(),
      params: reportId ? { reportId } : undefined,
    });
    return response.data;
  }

  async getOcrAutomationReportById(reportId: string): Promise<{ success: boolean; report: Record<string, unknown> }> {
    const response = await axios.get(`${this.baseURL}/documents/ocr/automation/${reportId}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getDocumentOcrValidation(id: string): Promise<{
    success: boolean;
    ocrValidation: OcrValidationPayload | null;
    ocrMetadata: Record<string, unknown> | null;
    warnings: string[];
    userAction: OcrUserAction | null;
  }> {
    const response = await axios.get(`${this.baseURL}/documents/${id}/ocr-validation`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    const mimeType = (file.type || '').toLowerCase();
    const fileName = (file.name || '').toLowerCase();
    const isSupportedMimeType = SUPPORTED_DOCUMENT_MIME_TYPES.has(mimeType);
    const hasSupportedExtension = SUPPORTED_DOCUMENT_EXTENSIONS.some((ext) => fileName.endsWith(ext));

    if (/\.(heic|heif)$/.test(fileName) || /heic|heif/.test(mimeType)) {
      return {
        valid: false,
        error: 'iPhone HEIC photos are not supported. Share the photo as JPG, or set Camera > Formats to "Most Compatible".',
      };
    }
    if (!isSupportedMimeType && !hasSupportedExtension) {
      return {
        valid: false,
        error: `Unsupported file type. Allowed types: ${SUPPORTED_DOCUMENT_TYPE_LABEL}`,
      };
    }

    const maxSize = UPLOAD_MAX_FILE_MB * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: `File size must be less than ${UPLOAD_MAX_FILE_MB}MB` };
    }

    return { valid: true };
  }

  /**
   * Validate a bill upload against the formats an admin currently allows (e.g. spreadsheets only).
   */
  validateBillFile(
    file: File,
    formats: Record<BillUploadFormat, boolean> = getAllowedBillUploadFormats()
  ): { valid: boolean; error?: string } {
    const fileName = (file.name || '').toLowerCase();
    const mimeType = (file.type || '').toLowerCase();
    if (/\.(heic|heif)$/.test(fileName) || /heic|heif/.test(mimeType)) {
      return this.validateFile(file);
    }
    const format = resolveBillUploadFormat(file);
    if (!format) {
      return { valid: false, error: `Unsupported file type. Allowed types: ${getBillUploadTypeLabel(formats)}` };
    }
    if (!formats[format]) {
      return {
        valid: false,
        error: `${BILL_FORMAT_LABELS[format]} files are not accepted for bills right now. Allowed types: ${getBillUploadTypeLabel(formats)}`,
      };
    }
    const maxSize = UPLOAD_MAX_FILE_MB * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: `File size must be less than ${UPLOAD_MAX_FILE_MB}MB` };
    }
    return { valid: true };
  }
}

const documentService = new DocumentService();
export default documentService;